// Scroll a focused input toward the upper third of the visible viewport so the
// user can see what they're typing once the soft keyboard (or our custom keypad)
// covers the bottom half of the screen.
//
// Why upper third (not the very top): keeping the input one row down preserves
// the visible label above it ("Quantity", "Customer name") so the user retains
// context — typing into a field whose label has scrolled away is disorienting.
//
// Uses visualViewport.height when available — that's the post-keyboard height on
// iOS / Chrome Android. Fallback to innerHeight for desktop and older browsers
// where the keyboard doesn't shrink the layout viewport.

const TARGET_FRACTION = 1 / 3;
// Skip tiny adjustments — they look like a flicker on slow phones and add no
// value if the input is already roughly where we want it.
const NOOP_THRESHOLD_PX = 8;
// Wait for the soft keyboard / on-screen keypad to finish animating in before
// measuring. iOS keyboard takes ~250ms; Chrome Android ~150ms. 250ms covers both.
const KEYBOARD_SETTLE_MS = 250;

// Returns a cancel function. Callers (component effects, blur handlers) must
// invoke it on cleanup so a pending scroll doesn't fire after the keypad has
// already closed or the element has unmounted — that would yank the page out
// from under the user.
export function scrollFocusedToUpperThird(
  el: HTMLElement,
  opts?: { delayMs?: number },
): () => void {
  // SSR / non-DOM environments (jsdom without window, future server pre-render):
  // bail out cleanly so importers don't crash on focus events fired in tests.
  if (typeof window === 'undefined') return () => {};
  const delay = opts?.delayMs ?? KEYBOARD_SETTLE_MS;
  const id = window.setTimeout(() => {
    // Element may have been unmounted between focus and the timer firing —
    // measuring a detached node returns rect.top=0 and would scroll the page
    // to the very top, which is worse than doing nothing.
    if (!document.contains(el)) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.visualViewport?.height ?? window.innerHeight;
    const targetTop = viewportH * TARGET_FRACTION;
    const delta = rect.top - targetTop;
    if (Math.abs(delta) < NOOP_THRESHOLD_PX) return;
    window.scrollBy({ top: delta, behavior: 'smooth' });
  }, delay);
  return () => window.clearTimeout(id);
}
